import produce from 'immer'
import {actionTypes} from './constants'

const initialState = {
  applications: {
    status: null,
    data: {
      byId: {},
      allIds: null,
      platinumIds: null,
      premiumIds: null
    }
  },
  audienceTypes: {
    status: null,
    data: {
      byId: {},
      allIds: []
    }
  },
  courseStandards: {
    status: null,
    data: {
      byId: {},
      allIds: []
    }
  },
  features: {
    status: null,
    data: {
      byId: {},
      allIds: []
    }
  },
  industries: {
    status: null,
    data: {
      byId: {},
      allIds: []
    }
  },
  organizationTypes: {
    status: null,
    data: {
      byId: {},
      allIds: []
    }
  },
  objectives: {
    status: null,
    data: {
      byId: {},
      allIds: []
    }
  },
  compare: {
    systemIds: []
  },
}

const initialApp = {
  type: '',
  isPublished: false,
  name: '',
  description: '',
  features: [],
  audienceTypes: [],
  courseStandards: [],
  deploymentTypes: [],
  playlistItems: [],
  caseStudies: [],
  id: 'new'
  // MongoDB will generate _id when creating a new record
}

const reducer = produce((draft, action) => {
  switch (action.type) {
    case actionTypes.FETCH_APPLICATIONS_REQUESTED:
      draft.applications.status = 'fetching'
      break

    case actionTypes.FETCH_APPLICATIONS_SUCCEEDED:
      draft.applications.status = 'succeeded'
      normalizeData(action.payload.results.data, draft.applications.data)
      break

    case actionTypes.FETCH_APPLICATIONS_FAILED:
      draft.applications.status = 'failed'
      break
    
    case actionTypes.FETCH_APPLICATION_DETAIL_SUCCEEDED: {
      const {_id: id} = action.payload

      // Merge or overwrite the existing item
      draft.applications.data.byId[id] = {
        ...draft.applications.data.byId[id],
        ...action.payload
      }

      // Ensure the ID is in allIds
      const set = new Set(draft.applications.data.allIds || [])
      set.add(id)
      draft.applications.data.allIds = Array.from(set)

      // Update editing state if we're currently editing this application
      if (draft.editing && draft.editing.data && draft.editing.data._id === id) {
        draft.editing.data = {
          ...draft.editing.data,
          ...action.payload
        }
      }

      break
    }

    case actionTypes.FETCH_AUDIENCE_TYPES_REQUESTED:
      draft.audienceTypes.status = 'fetching'
      break

    case actionTypes.FETCH_AUDIENCE_TYPES_SUCCEEDED:
      draft.audienceTypes.status = 'succeeded'
      normalizeData(action.payload.results.data, draft.audienceTypes.data)
      break

    case actionTypes.FETCH_AUDIENCE_TYPES_FAILED:
      draft.audienceTypes.status = 'failed'
      break

    case actionTypes.FETCH_COURSE_STANDARDS_REQUESTED:
      draft.courseStandards.status = 'fetching'
      break

    case actionTypes.FETCH_COURSE_STANDARDS_SUCCEEDED:
      draft.courseStandards.status = 'succeeded'
      normalizeData(action.payload.results.data, draft.courseStandards.data)
      break

    case actionTypes.FETCH_COURSE_STANDARDS_FAILED:
      draft.courseStandards.status = 'failed'
      break

    case actionTypes.FETCH_FEATURES_REQUESTED:
      draft.features.status = 'fetching'
      break

    case actionTypes.FETCH_FEATURES_SUCCEEDED:
      draft.features.status = 'succeeded'
      normalizeData(action.payload.results, draft.features.data)
      break

    case actionTypes.FETCH_FEATURES_FAILED:
      draft.features.status = 'failed'
      break

    case actionTypes.FETCH_INDUSTRIES_REQUESTED:
      draft.industries.status = 'fetching'
      break

    case actionTypes.FETCH_INDUSTRIES_SUCCEEDED:
      draft.industries.status = 'succeeded'
      normalizeData(action.payload.results.data, draft.industries.data)
      break

    case actionTypes.FETCH_INDUSTRIES_FAILED:
      draft.industries.status = 'failed'
      break

    case actionTypes.FETCH_ORGANIZATION_TYPES_REQUESTED:
      draft.organizationTypes.status = 'fetching'
      break

    case actionTypes.FETCH_ORGANIZATION_TYPES_SUCCEEDED:
      draft.organizationTypes.status = 'succeeded'
      normalizeData(action.payload.results.data, draft.organizationTypes.data)
      break

    case actionTypes.FETCH_ORGANIZATION_TYPES_FAILED:
      draft.organizationTypes.status = 'failed'
      break

    case actionTypes.FETCH_OBJECTIVES_REQUESTED:
      draft.objectives.status = 'fetching'
      break

    case actionTypes.FETCH_OBJECTIVES_SUCCEEDED:
      draft.objectives.status = 'succeeded'
      normalizeData(action.payload.results.data, draft.objectives.data)
      break

    case actionTypes.FETCH_OBJECTIVES_FAILED:
      draft.objectives.status = 'failed'
      break
      
    case actionTypes.RESET_EDIT_STATE:
      // Completely reset the editing state
      draft.editing = null
      break

    case actionTypes.EDIT_APPLICATION_BEGIN: {
      const {id} = action.payload
      let data

      // Always start with a null editing state to ensure no previous data is retained
      draft.editing = null
      
      if (id === 'new') {
        // For new applications, use a fresh copy of initialApp template
        // Make sure to create a completely new object to avoid any references to previous data
        data = JSON.parse(JSON.stringify(initialApp))
      } else if (id && draft.applications.data.byId[id]) {
        // For existing applications, use data from the store
        data = {...draft.applications.data.byId[id]}
      } else {
        // Fallback if no data is available
        data = JSON.parse(JSON.stringify(initialApp))
        data.id = id
      }

      // Set up the new editing state
      draft.editing = {
        data,
        status: 'editing'
      }
      break
    }

    case actionTypes.EDIT_APPLICATION_CHANGE:
      Object.assign(draft.editing.data, action.payload)
      break

    case actionTypes.EDIT_APPLICATION_ADD_CASE_STUDY:
      draft.editing.data.caseStudies.push(action.payload)
      draft.editing.data.lastCaseStudy = action.payload
      break

    case actionTypes.EDIT_APPLICATION_ADD_PLAYLIST_ITEM:
      draft.editing.data.playlistItems.push({
        ...action.payload
      })
      break

    case actionTypes.UPLOADING_FILE: {
      if (!draft.editing.data.uploadingFiles) {
        draft.editing.data.uploadingFiles = []
      }

      const existingIndex = draft.editing.data.uploadingFiles.findIndex(i => i.uploadUrl === action.payload.uploadUrl)
      if (existingIndex >= 0) {
        if (action.payload.done === true) {
          draft.editing.data.uploadingFiles.splice(existingIndex, 1)
        } else {
          draft.editing.data.uploadingFiles[existingIndex] = action.payload
        }
      } else {
        draft.editing.data.uploadingFiles.push(action.payload)
      }
    }
      break

    case actionTypes.SAVE_PLAYLIST_ITEM_ORDER_SUCCEEDED:
      action.payload.forEach((newItem,index) => {
        const itemIndex = draft.editing.data.playlistItems.findIndex(i => i.id === newItem.id)
        draft.editing.data.playlistItems[itemIndex].order = index
      })
      break

    case actionTypes.EDIT_PLAYLIST_ITEM_SUCCEEDED: {
      const itemIndex = draft.editing.data.playlistItems.findIndex(i => i.id === action.payload.id)
      draft.editing.data.playlistItems[itemIndex] = {
        ...draft.editing.data.playlistItems[itemIndex],
        ...action.payload
      }
    }
      break

    case actionTypes.FETCH_PLAYLIST_ITEM_VIEWS_SUCCEEDED:
      draft.playlistItemViews = action.payload.results.data
      break

    case actionTypes.DELETE_PLAYLIST_ITEM_SUCCEEDED: {
      const itemIndex = draft.editing.data.playlistItems.findIndex(i => i.id === action.payload.id)
      draft.editing.data.playlistItems.splice(itemIndex, 1)
      //Remove from the original as well
      const original = draft.applications.data.byId[draft.editing.data.id]
      const orginalIndex = original.playlistItems.findIndex(i => i.id === action.payload.id)
      original.playlistItems.splice(orginalIndex, 1)
    }
      break

    case actionTypes.EDIT_PLAYLIST_ITEM_THUMBNAIL_REQUESTED: {
      draft.editing.data.playlistItemThumbnail = action.payload.thumbnail
      break
    }

    case actionTypes.EDIT_APPLICATION_UPDATE_CASE_STUDY: {
      const {id, ...updates} = action.payload
      const index = draft.editing.data.caseStudies.findIndex(cs => cs.id === id)
      Object.assign(draft.editing.data.caseStudies[index], {...updates})

      const plIndex = draft.editing.data.playlistItems.findIndex(p => p.file?.id === id)
      if (plIndex > -1) {
        Object.assign(draft.editing.data.playlistItems[plIndex], {...updates})
      }
      break
    }

    case actionTypes.SAVE_APPLICATION_SUCCEEDED: {
      const {_id: id} = action.payload
      
      // Ensure we preserve important fields like the thumbnail if they exist in the current state
      const existingData = draft.applications.data.byId[id] || {}
      const existingThumbnail = existingData.thumbnail || {}

      // If the payload only has a thumbnail ID but not the full object with URI,
      // preserve the existing thumbnail data
      let thumbnail = action.payload.thumbnail
      if (thumbnail && typeof thumbnail === 'string' && existingThumbnail.uri) {
        thumbnail = {
          id: thumbnail,
          ...existingThumbnail
        }
      }

      // Update the state with the new data, preserving thumbnail information
      // Ensure both _id and id are set properly
      draft.applications.data.byId[id] = {
        ...existingData,
        ...action.payload,
        // Make sure id is also set, since some components expect 'id' instead of '_id'
        id: id, 
        thumbnail: thumbnail || existingThumbnail
      }
      
      // Make sure the ID is in the allIds array if it's a new application
      if (!draft.applications.data.allIds.includes(id)) {
        draft.applications.data.allIds.push(id)
      }

      const set = new Set(draft.applications.data.allIds)
      set.add(id)
      draft.applications.data.allIds = Array.from(set)

      break
    }

    case actionTypes.DELETE_APPLICATION_SUCCEEDED: {
      const {id} = action.payload
      
      // Remove the application from byId
      if (draft.applications.data.byId[id]) {
        delete draft.applications.data.byId[id]
      }
      
      // Remove the ID from allIds
      draft.applications.data.allIds = draft.applications.data.allIds.filter(appId => appId !== id)
      
      // Remove from premium/platinum lists if present
      if (draft.applications.data.premiumIds) {
        draft.applications.data.premiumIds = draft.applications.data.premiumIds.filter(appId => appId !== id)
      }
      
      if (draft.applications.data.platinumIds) {
        draft.applications.data.platinumIds = draft.applications.data.platinumIds.filter(appId => appId !== id)
      }
      
      break
    }

    case actionTypes.ADD_SYSTEMS_TO_COMPARE_LIST: {
      const ids = [action.payload.ids].flat()
      draft.compare.systemIds = Array.from(
        new Set([...draft.compare.systemIds, ...ids])
      )
      break
    }

    case actionTypes.REMOVE_SYSTEMS_FROM_COMPARE_LIST: {
      const ids = [action.payload.ids].flat()
      draft.compare.systemIds = draft.compare.systemIds.filter(
        id => !ids.includes(id)
      )
      break
    }


    // no default
  }
}, initialState)

const normalizeData = (source, target) => {
  const ids = new Set()

  const mostRecentSorter = (a, b) =>
    new Date(a.updatedAt) - new Date(b.updatedAt)

  const sortedApplications = source.sort(mostRecentSorter)

  sortedApplications.forEach(result => {
    ids.add(result.id)
    target.byId[result.id] = result

    if (result.children) {
      result.children.forEach(child => {
        target.byId[child.id] = child
      })
    }
  })

  const allIdsArray = Array.from(ids)

  const nameSorter = (a, b) =>
    target.byId[a].name.localeCompare(target.byId[b].name)

  const sortedApps = allIdsArray.reduce(
    (acc, id) => {
      const isPremium = target.byId[id].isPremium
      const isPlatinum = target.byId[id].isPlatinum

      if (isPlatinum) {
        acc.platinum.push(id)
      } else if (isPremium) {
        acc.premium.push(id)
      } else {
        acc.nonPremium.push(id)
      }
      return acc
    },
    {
      platinum: [],
      premium: [],
      nonPremium: []
    }
  )

  const sortedPlatinumAppsbyName = sortedApps.platinum.slice().sort(nameSorter)
  const sortedPremiumAppsbyName = sortedApps.premium.slice().sort(nameSorter)
  const sortedAppsbyName = sortedApps.nonPremium.slice().sort(nameSorter)

  target.allIds = [...sortedPlatinumAppsbyName, ...sortedPremiumAppsbyName, ...sortedAppsbyName]
  target.platinumIds = sortedPlatinumAppsbyName
  target.premiumIds = sortedPremiumAppsbyName

}

export default reducer
